import { AxiosError, AxiosResponse } from "axios";
import { getDefaultStore } from "jotai";
import { getAuthToken } from "../utils/cookieStorage";
import store from "../store/configureStore";
import { authorizeActions, globalActions } from "../store/actions";
import { toast } from "react-toastify";
import { exemptedMessages } from "./exemptedMessages";

export const headerify = async (request: any) => {
  store.store.dispatch(globalActions.showTopLoading());

  const token = getAuthToken();

  return {
    ...request,
    headers: {
      ...request.headers,
      Authorization: token ? `Bearer ${token}` : null
    }
  };
};

const forbiddenExempted = (): boolean => {
  const pathname = window.location.pathname;

  return (
    pathname.split("/").includes("posts") || 
    pathname.split("/").includes("library") || 
    pathname.split("/").includes("lectures") || 
    pathname.split("/").includes("courses") || 
    pathname.split("/").includes("events") ||
    pathname.split("/")[1] === "pages-preview" ||
    pathname.split("/")[2] === "onboarding"
  );
}

const isUpgradeRequiredError = (error: AxiosError): boolean => {
  const data = error.response && error.response.data;

  return !!(data && data.errors && data.errors.code === "UPGRADE_REQUIRED");
};

const openBillingPlansPopup = (message: string) => {
  // Loads showMessageMenuAtom on first use, so the SSR server never loads it.
  if (typeof window === "undefined") {
    return;
  }

  const title = message
    ? message
    : "You have reached your Free plan limit. Upgrade to continue managing your community.";

  import("../showMessageMenuAtom").then(({ communityBillingPlansPopupAtom }) => {
    getDefaultStore().set(communityBillingPlansPopupAtom as any, { open: true, title });
  });
};

const callerToastsError = (error: AxiosError): boolean => {
  const config = error.response && error.response.config;
  const url = config && config.url;
  const method = config && config.method;

  if (config && (config as any).silentError) return true;

  // the test-email and checkout sign-in endpoints: their callers toast the backend message themselves
  return !!(
    url &&
    method &&
    method.toLowerCase() === "post" &&
    (/\/groups\/[^/]+\/custom-emails\/test$/.test(url) || /\/web-login(-code)?$/.test(url))
  );
};

const deadTokenMessages = ["Invalid authentication", "Unauthenticated."];

export const handleError = (error: AxiosError) => {
  store.store.dispatch(globalActions.hideTopLoading());

  if (!error || !error.response) {
    return;
  }

  if (callerToastsError(error)) {
    return Promise.reject(error.response);
  }

  if (isUpgradeRequiredError(error)) {
    openBillingPlansPopup(error.response.data.message);

    // The popup carries the message, so strip it from the rejection: callers
    // guard their own toasts on error.data.message, and dropping it skips the
    // redundant toast while leaving the rest of their cleanup to run.
    return Promise.reject({
      ...error.response,
      data: { ...error.response.data, message: undefined }
    });
  }

  if (error.response.data && error.response.data.errors) {
    if(
      window.location.pathname.split("/")[1] === "new-password" ||
      window.location.pathname.split("/")[1] === "new-name"
    ) {
      return  Promise.reject(error.response);
    }

    const groupAppErrors = error.response.data.errors;
    const firstErrorKey = Object.keys(error.response.data.errors)[0];
    
    const emailId422ErrorCheck = (error.response.config.url && error.response.config.url.includes('email-identities')) && error.response.status === 422

    if(firstErrorKey) {
      const displayError = groupAppErrors[firstErrorKey][0];
      if(error.response.status !== 400 && error.response.status !== 403){
        if (!emailId422ErrorCheck) {
          toast.error(displayError, {
            autoClose: 3000,
            position: "top-center",
            hideProgressBar: true,
            theme: "light",
          });
        }
      }
    }
  }

  switch (error.response.status) {
    case 400:
      if(
        error.config.url &&
        error.config.url.includes('pages') &&
        error.config.url.includes('publish')
      ) {
        return Promise.reject(error.response);
      }
      toast.error(`${error.response.data.message}`, {
        // type: 'error',
        autoClose: 3000,
        position: "top-center",
        hideProgressBar: true,
        theme: "light",
      });
      return Promise.reject(error.response);
    case 401:
      // Passport rejected the stored token, so the session is dead.
      if (getAuthToken() && error.response.data && deadTokenMessages.includes(error.response.data.message)) {
        store.store.dispatch(authorizeActions.dbLogout() as any);
      }
      return Promise.reject(error.response);
    case 404:
      return Promise.reject(error.response);
    case 422:
      return Promise.reject(error.response);
    case 403: {
      // override for library, lectures, events and content that can be duplicated
      if (error.response && error.response.data && error.response.data.message && error.response.data.message.includes("Storage not enough")) {
        return Promise.reject(error.response);
      }
      if (forbiddenExempted()) {
        return Promise.reject(error.response);
      }
      if (error.response.config.url && !error.response.config.url.includes("pinned") && !error.response.config.url.includes('s3/sign') && !error.response.config.url.includes('courses')) {
        if (error.response.config.url && error.response.config.url.includes("posts") && error.response.config.url.includes("postable")) {
        } else {

          if(
            window.location.pathname.split("/").includes("library") && 
            window.location.pathname.split("/").includes("lectures") &&
            exemptedMessages.includes(error.response.data.message)
          ) {
            
          } else {
            if (
              (window.location.pathname.split("/").includes("channel") &&
                exemptedMessages.includes(error.response.data.message))
              ||
              (error.response.config.url && error.response.config.url.includes("featured"))
            ) {

            }
            else {
              if (
                error.response.data.message.includes('User is blocked in community.')
              ) {
              }
              else {
                toast.error(`${error.response.data.message}`, {
                  // type: 'error',
                  autoClose: 3000,
                  position: "top-center",
                  hideProgressBar: true,
                  theme: "light",
                });
              }
            }
          }
          
        }
      }

      return Promise.reject(error.response);
    }
    default: {
      return Promise.reject(error.response);
    }
  }
};

export const handleResponse = (response: AxiosResponse) => {
  store.store.dispatch(globalActions.hideTopLoading());
  return response.data;
};

export const createQueryString = (object: any) => {
  if (!object || Array.isArray(object) || typeof object === "string") {
    return Promise.reject({ error: "Something went wrong" });
  }
  let str = "";
  Object.keys(object).forEach(
    (key, i) =>
      (str = `${str}${key}=${object[key]}${
        i < Object.keys(object).length - 1 ? "&" : ""
      }`)
  );
  return str;
};

export const createDynamicRouteRequest = (
  httpClient: any,
  getRouteFn: any,
  staticConfig = {}
) => (getRouteArgument: any, { body = {}, dynamicConfig = {} } = {}) => {
  const route = getRouteFn(getRouteArgument);
  const config = {
    ...staticConfig,
    ...dynamicConfig
  };
  return body ? httpClient(route, body, config) : httpClient(route, config);
};

export const createSimpleRequest = (
  httpClient: any,
  route: string,
  staticConfig = {}
) => ({ body = {}, dynamicConfig = {} } = {}) => {
  const config = {
    ...staticConfig,
    ...dynamicConfig
  };
  return body ? httpClient(route, body, config) : httpClient(route, config);
};

export const createQueryRequest = (
  httpClient: any,
  route: string,
  staticConfig = {}
) => (params: any, dynamicConfig = {}) => {
  const config = {
    ...staticConfig,
    ...dynamicConfig
  };
  const query = createQueryString(params);
  const url = `${route}${query}`;
  return httpClient(url, config);
};
